import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../apiClient';
import ActionButton from '../ui/ActionButton';
import CampaignTracker from './CampaignTracker';
import './SegmentComposer.css';

/* Audience-agnostic segment composer — build full, ship gated.
   The audience config (see ./audiences.js) supplies endpoints, filter
   groups, and copy; this component supplies the flow: identity block,
   filters, structural-exclusion mathline, templates, AI draft, and the
   gated send. The exclusion math is server-side and STRUCTURAL: the
   sendable list can never contain an excluded row. SMS is hard-locked
   backend-side; the UI says so. */

function loadDraft(key) {
  try { return JSON.parse(sessionStorage.getItem(key) || 'null') || {}; }
  catch { return {}; }
}

// Collapse any run of braces around a merge token to exactly two.
// Idempotent — legacy {first_name}, correct {{first_name}}, and
// over-braced {{{first_name}}} (a template normalized twice) all come
// out as {{first_name}}, which is what the backend renderer knows.
const normalizeMergeFields = (x) => String(x || '').replace(/\{+(\w+)\}+/g, '{{$1}}');

// Pre-extraction lead drafts stored stages/sources at the top level;
// fold them into the generic selection map so no in-flight draft is lost.
function initialSelection(audience, draft, presetFilters) {
  const sel = {};
  audience.filterGroups.forEach((g) => {
    sel[g.key] = presetFilters?.[g.key]?.length
      ? presetFilters[g.key]
      : (draft.sel?.[g.key] || draft[g.key] || []);
  });
  return sel;
}

export default function SegmentComposer({ open, onClose, audience, presetFilters }) {
  const navigate = useNavigate();
  const d = loadDraft(audience.draftKey);
  const [sel, setSel] = useState(() => initialSelection(audience, d, presetFilters));
  const [from, setFrom] = useState(d.from || '');
  const [to, setTo] = useState(d.to || '');
  const [cooldown, setCooldown] = useState(d.cooldown || 14);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);
  const [subject, setSubject] = useState(d.subject || '');
  const [body, setBody] = useState(d.body || 'Hi {{first_name}},\n\n');
  const [confirming, setConfirming] = useState(false);
  const [trackId, setTrackId] = useState(null);
  const [aiGoal, setAiGoal] = useState('');
  // Campaign goal — persisted with the campaign and fed to the reply-draft
  // prompt, so replies argue toward what Kyle actually wanted.
  const [campaignGoal, setCampaignGoal] = useState(d.campaignGoal || '');
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [identity, setIdentity] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [channel, setChannel] = useState('email');
  // Once Kyle picks a channel, respect it. Until then we auto-default to the
  // channel the audience can actually receive (recommended by the preview).
  const [channelTouched, setChannelTouched] = useState(false);
  const pickChannel = (c) => { setChannel(c); setChannelTouched(true); };
  const [mediaUrl, setMediaUrl] = useState('');
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [quoteStatus, setQuoteStatus] = useState(presetFilters?.quote_status ?? (d.quoteStatus || ''));
  const [quoteFrom, setQuoteFrom] = useState(d.quoteFrom || '');
  const [quoteTo, setQuoteTo] = useState(d.quoteTo || '');

  const filters = useMemo(() => ({
    ...sel,
    created_from: from || undefined,
    created_to: to || undefined,
    quote_status: quoteStatus || undefined,
    quote_sent_from: quoteFrom || undefined,
    quote_sent_to: quoteTo || undefined,
    // "Outreach these →": explicit row selection from a list page.
    ...(presetFilters?.customer_ids?.length ? { customer_ids: presetFilters.customer_ids } : {}),
  }), [sel, from, to, quoteStatus, quoteFrom, quoteTo, presetFilters]);

  /* GSM-7 packs 160 (153 concatenated); anything non-GSM goes UCS-2 at 70/67. */
  const smsSegments = useMemo(() => {
    const text = body || '';
    const gsm = /^[\x0A\x0D\x20-\x7E]*$/.test(text);
    const single = gsm ? 160 : 70;
    const multi = gsm ? 153 : 67;
    const count = text.length <= single ? (text.length ? 1 : 0) : Math.ceil(text.length / multi);
    return { chars: text.length, count, gsm };
  }, [body]);

  // Draft survives navigation: click through to a row, come back, nothing
  // is lost. The host page auto-reopens the panel when a draft is open.
  useEffect(() => {
    try {
      sessionStorage.setItem(audience.draftKey, JSON.stringify({
        open, sel, from, to, cooldown, subject, body, campaignGoal,
      }));
    } catch { /* ignore */ }
  }, [open, sel, from, to, cooldown, subject, body, audience.draftKey]);

  useEffect(() => {
    const preset = {};
    let any = false;
    audience.filterGroups.forEach((g) => {
      if (presetFilters?.[g.key]?.length) { preset[g.key] = presetFilters[g.key]; any = true; }
    });
    if (any) setSel((s) => ({ ...s, ...preset }));
    // Entry points (e.g. the Quotes page) hand us the FILTER, not row ids —
    // "Sent, no answer" arrives as quote_status='sent'. Apply it so the
    // opened composer is already the segment Kyle was looking at.
    if (presetFilters?.quote_status !== undefined) setQuoteStatus(presetFilters.quote_status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetFilters]);

  // Default to the channel the audience can actually receive — until Kyle
  // picks one himself. Never silently keep the lens that excludes them.
  useEffect(() => {
    if (channelTouched) return;
    const rec = preview?.recommended_channel;
    if (rec && rec !== channel) setChannel(rec);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview?.recommended_channel, channelTouched]);

  useEffect(() => {
    if (!open) return;
    api.get('/comms/sending-identity/').then(r => setIdentity(r.data)).catch(() => {});
    api.get('/templates/templates/').then(r => {
      const rows = r.data?.results || (Array.isArray(r.data) ? r.data : []);
      setTemplates(rows.filter(t => t.channel === 'email'));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setLoading(true);
    const t = setTimeout(() => {
      api.post(audience.endpoints.preview, { filters, channel, cooldown_days: cooldown })
        .then(r => { if (live) setPreview(r.data); })
        .catch(() => live && toast.error('Preview failed'))
        .finally(() => live && setLoading(false));
    }, 350);
    return () => { live = false; clearTimeout(t); };
  }, [open, filters, cooldown, channel, audience.endpoints.preview]);

  if (!open) return null;

  const draftWithAi = async (toneHint) => {
    if (!aiGoal.trim() && !toneHint) { toast.info('Tell the AI the goal first'); return; }
    setDrafting(true);
    try {
      const r = await api.post(audience.endpoints.draft, {
        goal: aiGoal.trim() || subject || audience.defaultAiGoal,
        tone_hint: toneHint || '', filters,
      });
      setSubject(r.data.subject || '');
      setBody(r.data.body || '');
      toast.success('Draft ready — edit and approve');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Draft failed');
    } finally { setDrafting(false); }
  };

  const applyTemplate = (t) => {
    if (!t) { setSubject(''); return; }  // clearing the template must clear its subject, not leave it stale
    setSubject(normalizeMergeFields(t.subject || t.metadata?.subject || t.name));
    setBody(normalizeMergeFields(t.body));
  };

  const saveTemplate = async () => {
    const name = window.prompt('Template name:', subject || 'Outreach template');
    if (!name) return;
    try {
      await api.post('/templates/templates/', {
        name, subject, body, channel: 'email', metadata: { subject },
      });
      toast.success('Template saved');
      const r = await api.get('/templates/templates/');
      const rows = r.data?.results || (Array.isArray(r.data) ? r.data : []);
      setTemplates(rows.filter(t => t.channel === 'email'));
    } catch { toast.error('Could not save template'); }
  };

  const toggle = (key, v) =>
    setSel(s => ({ ...s, [key]: s[key].includes(v) ? s[key].filter(x => x !== v) : [...s[key], v] }));

  const onPickMedia = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Twilio caps MMS media at 5MB — pick a smaller image.');
      return;
    }
    setUploadingMedia(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('category', 'mms');
      const r = await api.post('/storage/files/upload/', form,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = r.data?.url || r.data?.file?.url || '';
      if (!url) throw new Error('upload returned no url');
      setMediaUrl(url);
      toast.success('Image attached');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploadingMedia(false);
    }
  };

  const breakdownLine = preview ? Object.entries(preview.excluded_breakdown || {})
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${n} ${audience.exclusionLabels[k] || k}`)
    .join(', ') : '';

  const send = async (testSend) => {
    setSending(true);
    try {
      const r = await api.post(audience.endpoints.send, {
        filters, channel, cooldown_days: cooldown,
        goal: campaignGoal.trim(),
        subject: normalizeMergeFields(subject), body: normalizeMergeFields(body),
        test_send: testSend,
        confirmed: !testSend, test_to: 'khalis@abon.ai',
      });
      if (testSend) toast.success(r.data.sent === false ? 'Test failed' : 'Test sent to khalis@abon.ai');
      else toast.success(`Queued to ${r.data.recipients} recipients`);
      setConfirming(false);
      // Open the live tracker for a real send so it can be watched as it runs —
      // both audiences feed the same tracker (one engine, leads and customers).
      if (!testSend && r.data?.campaign_id) {
        setTrackId(r.data.campaign_id);
      }
    } catch (e) {
      const err = e?.response?.data;
      toast.error(err?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  const goToRow = (row) => navigate(audience.rowPath(row));

  if (trackId) {
    return (
      <CampaignTracker
        campaignId={trackId}
        onClose={() => { setTrackId(null); onClose?.(); }}
      />
    );
  }

  return (
    <div className="orx-overlay" role="dialog" aria-modal="true">
      <div className="orx-panel">
        <div className="orx-head">
          <div>
            <h3>{audience.title}</h3>
            <p>{audience.subtitle}</p>
          </div>
          <button className="orx-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {preview && !preview.outreach_enabled && (
          <div className="orx-gate">
            Outreach is <b>OFF</b> for this tenant (<code>outreach_enabled</code> flag).
            Build is live but only the allow-listed test send works until it's flipped.
          </div>
        )}

        {/* Sender identity — who this lands as, where replies go. Renders a
            selector automatically if the API ever returns multiple identities. */}
        <div className="orx-identity">
          <div className="orx-id-row">
            <span className="orx-label">From</span>
            {Array.isArray(identity) ? (
              <select className="orx-id-select">
                {identity.map(i => <option key={i.from_address}>{i.from_address}</option>)}
              </select>
            ) : (
              <span className="orx-id-value">{identity?.from_address || '—'}</span>
            )}
            {identity?.is_tenant_domain != null && (
              <span className={`orx-id-state ${identity.is_tenant_domain ? 'ok' : 'warn'}`}>
                {identity.is_tenant_domain ? '✓ your domain' : 'platform domain'}
              </span>
            )}
          </div>
          <div className="orx-id-row">
            <span className="orx-label">Replies</span>
            <span className="orx-id-value">{identity?.reply_to || '—'}</span>
            <span className="orx-id-note">land in your CRM inbox thread</span>
          </div>
          <div className="orx-id-row">
            <span className="orx-label">SMS</span>
            <span className="orx-id-note">sending number appears here when SMS unlocks</span>
            <a className="orx-link" href="/settings/communications" onClick={(e) => { e.preventDefault(); navigate('/settings/communications'); }}>
              change in Settings →
            </a>
          </div>
        </div>

        <div className="orx-filters">
          {audience.filterGroups.map((g, gi) => (
            <div className="orx-group" key={g.key}>
              <span className="orx-label">{g.label}</span>
              {g.options.map(v => (
                <button key={v} className={`orx-chip ${sel[g.key].includes(v) ? 'on' : ''}`}
                  onClick={() => toggle(g.key, v)}>{g.display ? g.display(v) : v}</button>
              ))}
              {gi === audience.filterGroups.length - 1 && (
                <>
                  <span className="orx-label" style={{ marginLeft: 12 }}>Created</span>
                  <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
                  <span className="orx-dash">–</span>
                  <input type="date" value={to} onChange={e => setTo(e.target.value)} />
                  <span className="orx-label" style={{ marginLeft: 12 }}>Cooldown</span>
                  <input type="number" min="1" max="90" value={cooldown}
                    onChange={e => setCooldown(Number(e.target.value) || 14)} style={{ width: 56 }} />
                  <span className="orx-dash">days</span>
                </>
              )}
            </div>
          ))}
          {audience.quoteFilter && (
            <div className="orx-group">
              <span className="orx-label">Quote</span>
              {[['', 'any'], ['sent', 'sent'], ['accepted', 'accepted'], ['declined', 'declined'],
                ['expired', 'expired'], ['no_quote', 'no quote']].map(([v, label]) => (
                <button key={v || 'any'} className={`orx-chip ${quoteStatus === v ? 'on' : ''}`}
                  onClick={() => setQuoteStatus(v)}>{label}</button>
              ))}
              <span className="orx-label" style={{ marginLeft: 12 }}>Quote sent</span>
              <input type="date" value={quoteFrom} onChange={e => setQuoteFrom(e.target.value)} />
              <span className="orx-dash">–</span>
              <input type="date" value={quoteTo} onChange={e => setQuoteTo(e.target.value)} />
              <button className="orx-chip"
                title="Sent a quote, never accepted, 30+ days ago, reachable by text — the revival segment"
                onClick={() => {
                  const cut = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
                  // The revival segment is quote-status ONLY: a lingering
                  // Status/Book chip (e.g. 'customer', 3 rows) would AND the
                  // set to ~0. Clear them so the base is every quote-sent party.
                  setSel(Object.fromEntries(audience.filterGroups.map((g) => [g.key, []])));
                  setQuoteStatus('sent'); setQuoteFrom(''); setQuoteTo(cut);
                  // These people have PHONES, not emails — the segment is SMS.
                  // On the email lens it collapses to ~8; on SMS it is the ~154.
                  setChannel('sms');
                }}>
                revival: sent, no answer, 30d+
              </button>
            </div>
          )}
        </div>

        {/* the exclusion math, on screen */}
        <div className="orx-mathline">
          {loading ? <span className="orx-computing">Computing segment…</span> : preview ? (
            <>
              <div className="orx-count">
                <span className="orx-count-num">{preview.sendable_count}</span>
                <div className="orx-count-side">
                  <span className="orx-count-label">
                    sendable {channel === 'email' ? 'by email' : `by ${channel.toUpperCase()}`}
                  </span>
                  <span className="orx-count-meta">
                    {preview.total_selected} selected · <b className="warn">{preview.excluded_count}</b> excluded
                  </span>
                </div>
              </div>
              {breakdownLine ? <div className="orx-breakdown">{breakdownLine}</div> : null}
              {preview.targeting_summary && (
                <div className="orx-targeting">
                  <span className="orx-targeting-k">Who gets this</span>
                  <span className="orx-targeting-v">{preview.targeting_summary}</span>
                </div>
              )}
              {preview.merge_field_coverage && (() => {
                const used = new Set(
                  ((subject + ' ' + body).match(/\{\{\s*(\w+)\s*\}\}/g) || [])
                    .map(t => t.replace(/[{}\s]/g, ''))
                );
                const rows = Object.entries(preview.merge_field_coverage)
                  .filter(([k]) => used.has(k));
                if (!rows.length) return null;
                return (
                  <div className="orx-coverage">
                    {rows.map(([k, v]) => (
                      <span key={k} className="orx-note">
                        {'{{'}{k}{'}}'}: {v.have} have a value · {v.fallback} get “{v.fallback_value}”
                      </span>
                    ))}
                  </div>
                );
              })()}
              {preview.excluded_count > 0 && (
                <button className="orx-link" onClick={() => setShowExcluded(v => !v)}>
                  {showExcluded ? 'hide excluded' : 'view excluded'}
                </button>
              )}
            </>
          ) : '—'}
        </div>

        {showExcluded && preview && (
          <div className="orx-list">
            {(preview.excluded || []).map(l => (
              <div key={l.id} className="orx-row orx-click" role="link" tabIndex={0}
                onClick={() => goToRow(l)}
                onKeyDown={e => e.key === 'Enter' && goToRow(l)}>
                <span className="who">{l.name}</span>
                <span className="what">{audience.exclusionLabels[l.excluded_reason] || l.excluded_reason}</span>
                <span className="meta">{l.email || l.phone || '—'}</span>
              </div>
            ))}
          </div>
        )}

        <div className="orx-compose">
          <div className="orx-ai">
            <span className="orx-label">Draft with AI</span>
            <input className="orx-subject" style={{ flex: 1 }} value={aiGoal}
              placeholder={audience.aiGoalPlaceholder}
              onChange={e => setAiGoal(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && draftWithAi()} />
            <button className="orx-chip on" disabled={drafting} onClick={() => draftWithAi()}>
              {drafting ? 'Drafting…' : '✨ Draft'}
            </button>
            {['shorter', 'warmer', 'more direct'].map(t => (
              <button key={t} className="orx-chip" disabled={drafting} title={`Regenerate ${t}`}
                onClick={() => draftWithAi(t)}>{t}</button>
            ))}
          </div>
          <div className="orx-note">AI drafts land here for your edit — nothing ever sends without the same approval gates.</div>
          <div className="orx-group">
            <span className="orx-label">Template</span>
            <select className="orx-id-select" defaultValue=""
              onChange={e => applyTemplate(templates.find(t => String(t.id) === e.target.value))}>
              <option value="">— pick a template —</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button className="orx-chip" onClick={saveTemplate} disabled={!body.trim()}>
              Save as template
            </button>
            <button className="orx-link" onClick={() => navigate('/settings/communications')}>
              manage templates →
            </button>
          </div>
          <div className="orx-group orx-goalrow">
            <span className="orx-label">Campaign goal</span>
            <input
              className="orx-goal"
              placeholder="e.g. get them to book a walkthrough"
              value={campaignGoal}
              onChange={(e) => setCampaignGoal(e.target.value)}
            />
            <span className="orx-note">
              steers the AI reply drafts when people write back — a human still approves every one
            </span>
          </div>
          <div className="orx-channels">
            {/* Channel picker. SMS is selectable when the TENANT is capable
                (own number / A2P) so the composer can be built against real
                segment counts — but SENDING stays server-locked (423) until
                the founder flips it. Showing capability is fine; sending is not. */}
            <button className={`orx-chip ${channel === 'email' ? 'on' : ''}`}
              onClick={() => pickChannel('email')}>Email</button>
            {preview?.sms_capable ? (
              <button className={`orx-chip ${channel === 'sms' ? 'on' : ''}`}
                title="SMS sends through the reviewed manual path — gated by outreach access and suppression, same as email."
                onClick={() => pickChannel('sms')}>
                SMS
              </button>
            ) : (
              <span className="orx-chip locked"
                title="SMS needs the tenant's own Twilio number / A2P registration. Not configured for this tenant.">
                SMS — not set up
              </span>
            )}
            {preview?.sms_capable && (
              <button className={`orx-chip ${channel === 'mms' ? 'on' : ''}`}
                title="MMS ≈ 3× SMS cost. Same reviewed manual path + gates as SMS."
                onClick={() => pickChannel('mms')}>
                MMS
              </button>
            )}
            {preview?.sendable_by_channel && (
              <span className="orx-reach"
                title="How many of this exact segment can actually receive on each channel">
                <b className="ok">{preview.sendable_by_channel.sms}</b> by SMS
                <span className="orx-dash"> · </span>
                <b>{preview.sendable_by_channel.email}</b> by email
              </span>
            )}
            {(channel === 'sms' || channel === 'mms') && (
              <span className="orx-note">
                {smsSegments.chars} chars · {smsSegments.count} segment{smsSegments.count === 1 ? '' : 's'}
                {smsSegments.gsm ? '' : ' (unicode)'}
              </span>
            )}
            {channel === 'mms' && (
              <>
                <label className="orx-chip" title="Attach an image (jpg/png/gif, ≤5MB — Twilio's cap)">
                  {uploadingMedia ? 'Uploading…' : (mediaUrl ? 'Replace image' : 'Attach image')}
                  <input type="file" accept="image/jpeg,image/png,image/gif" style={{ display: 'none' }}
                    onChange={onPickMedia} disabled={uploadingMedia} />
                </label>
                {mediaUrl && <span className="orx-note">image attached ✓</span>}
              </>
            )}
          </div>
          <div className="orx-group">
            <span className="orx-label">Insert field</span>
            {['{{first_name}}', '{{name}}'].map(f => (
              <button key={f} className="orx-chip" title={`Insert ${f} into the message`}
                onClick={() => setBody(b => b + f)}>{f}</button>
            ))}
            <span className="orx-note">rendered per recipient at send time</span>
          </div>
          {/* Subject is EMAIL-ONLY — SMS/MMS have no subject line. */}
          {channel === 'email' && (
            <input className="orx-subject" placeholder="Subject — e.g. Your quote is ready, {{first_name}}"
              value={subject} onChange={e => setSubject(e.target.value)} />
          )}
          <textarea className="orx-body" rows={6} value={body} onChange={e => setBody(e.target.value)}
            placeholder={'Hi {{first_name}},\n\nWrite your message — every {{field}} renders per recipient.'} />
          {channel === 'email' && (
            <div className="orx-note">An unsubscribe link is appended to every email automatically (CAN-SPAM).</div>
          )}
        </div>

        {preview && preview.sendable_count > 0 && (
          <div className="orx-list orx-recipients">
            <div className="orx-label" style={{ marginBottom: 6 }}>Final recipient list ({preview.sendable_count})</div>
            {(preview.sendable || []).map(l => (
              <div key={l.id} className="orx-row orx-click" role="link" tabIndex={0}
                onClick={() => goToRow(l)}
                onKeyDown={e => e.key === 'Enter' && goToRow(l)}>
                <span className="who">{l.name}</span>
                <span className="meta">{l.email}</span>
                <span className="what">{l.stage}</span>
              </div>
            ))}
            {preview.sendable_count > (preview.sendable || []).length && (
              <div className="orx-note">…and {preview.sendable_count - preview.sendable.length} more</div>
            )}
          </div>
        )}

        <div className="orx-actions">
          {channel !== 'email' && (
            <span className="orx-note">
              {channel.toUpperCase()} sends through the reviewed manual path — gated by
              outreach access and suppression, same as email. A human approves every reply.
            </span>
          )}
          <ActionButton variant="secondary" onClick={() => send(true)}
            disabled={sending || (channel === 'email' && !subject) || !body || channel !== 'email'}>
            Test send → khalis@abon.ai
          </ActionButton>
          <ActionButton variant="primary"
            disabled={sending || (channel === 'email' && !subject) || !body || !preview?.sendable_count
              || !preview?.outreach_enabled}
            onClick={() => setConfirming(true)}>
            Send to {preview?.sendable_count ?? 0} {audience.noun}…
          </ActionButton>
        </div>

        {confirming && (
          <div className="orx-confirm">
            <p>
              Send to <b>{preview?.sendable_count}</b> {audience.noun}{' '}
              <b>by {channel === 'email' ? 'email' : channel.toUpperCase()}</b>
              {channel === 'email' && subject ? <> — subject <b>“{subject}”</b></> : ''}?
              {' '}Excluded ({preview?.excluded_count}) stay excluded — structurally.
            </p>
            <div className="orx-actions">
              <ActionButton variant="secondary" onClick={() => setConfirming(false)}>Cancel</ActionButton>
              <ActionButton variant="danger" onClick={() => send(false)} disabled={sending}>
                Yes — send now
              </ActionButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
